import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogTitle, 
  IconButton, 
  Box, 
  Typography,
  Button,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Stepper,
  Step,
  StepLabel,
  alpha,
  Paper,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Chip,
  Stack,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CategoryIcon from '@mui/icons-material/Category';
import ImageIcon from '@mui/icons-material/Image';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { v4 as uuidv4 } from 'uuid';
import { mapConfig } from '../../../config/mapConfig'; // Import mapConfig
import axios from 'axios';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import CheckIcon from '@mui/icons-material/Check';
import SaveIcon from '@mui/icons-material/Save';
import InlineInspectionDetailsForm from '../forms/InlineInspectionDetailsForm';
import {
  BATCH_UPLOAD_LIMITS,
  validateFiles,
  buildRejectionSummary,
  buildAcceptAttribute,
  describeLimits,
  describeUploadError,
  formatFileSize,
} from '../../../utils/uploadValidation';

/**
 * BatchUploadModal component for uploading multiple images with GPS metadata extraction
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @returns {React.ReactElement} Batch upload modal component
 */
const BatchUploadModal = ({ open, onClose }) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  
  // States for categories and types
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState('');
  
  // Add loading and error states
  const [loading, setLoading] = useState({
    categories: false,
    types: false,
    upload: false
  });
  const [error, setError] = useState({
    categories: null,
    types: null,
    upload: null,
    selection: [] // human-readable lines for files skipped by client-side validation
  });
  
  // Track which step we're on
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Select Asset Category', 'Select Asset Type', 'Upload Images', 'Review Batch'];
  
  // File upload states
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [batchId, setBatchId] = useState('');
  const [dragActive, setDragActive] = useState(false);
  
  // States for polling photo statuses
  const [isPollingStatus, setIsPollingStatus] = useState(false);
  const [pollingError, setPollingError] = useState(null);
  const pollingIntervalRef = useRef(null); // To store interval ID
  const [uploadCompletedSuccessfully, setUploadCompletedSuccessfully] = useState(false);
  
  // States for Review Step
  const [reviewData, setReviewData] = useState(null);
  const [loadingReview, setLoadingReview] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  
  // State for managing which asset group's inspection detail form is expanded
  const [expandedAssetGroupId, setExpandedAssetGroupId] = useState(null); 
  
  // Generate a new batch ID when modal opens
  useEffect(() => {
    if (open) {
      setBatchId(uuidv4());
    }
  }, [open]);
  
  // Fetch categories from API - similar to FilterPanel approach
  useEffect(() => {
    const fetchCategories = async () => {
      if (!open) return; // Only fetch when modal is open
      
      setLoading(prev => ({ ...prev, categories: true }));
      setError(prev => ({ ...prev, categories: null }));
      
      try {
        const url = `${mapConfig.apiBaseUrl}${mapConfig.apiEndpoints.assetCategories}`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch categories: ${response.status}`);
        }
        
        const data = await response.json();
        setCategories(data);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError(prev => ({ ...prev, categories: err.message }));
      } finally {
        setLoading(prev => ({ ...prev, categories: false }));
      }
    };
    
    fetchCategories();
  }, [open]);
  
  // Fetch types based on selected category
  useEffect(() => {
    const fetchTypes = async () => {
      if (!selectedCategoryId) {
        setTypes([]);
        return;
      }
      
      setLoading(prev => ({ ...prev, types: true }));
      setError(prev => ({ ...prev, types: null }));
      
      try {
        const url = `${mapConfig.apiBaseUrl}${mapConfig.apiEndpoints.assetTypes}?category_id=${selectedCategoryId}`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch types: ${response.status}`);
        }
        
        const data = await response.json();
        setTypes(data);
      } catch (err) {
        console.error('Error fetching types:', err);
        setError(prev => ({ ...prev, types: err.message }));
      } finally {
        setLoading(prev => ({ ...prev, types: false }));
      }
    };
    
    fetchTypes();
  }, [selectedCategoryId]);
  
  // Handle category change
  const handleCategoryChange = (event) => {
    setSelectedCategoryId(event.target.value);
    setSelectedTypeId(''); // Reset the type when category changes
  };
  
  // Handle type change
  const handleTypeChange = (event) => {
    setSelectedTypeId(event.target.value);
  };
  
  // Handle navigation between steps
  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      // If on the last step (Review Batch), "Next" becomes "Finish" or triggers further action
      // For now, it will just close the modal if it's the absolute last step envisioned for this modal.
      // If review leads to another modal/page, that logic would go here.
      handleClose(); 
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };
  
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  const handleReset = () => {
    setActiveStep(0);
    setSelectedCategoryId('');
    setSelectedTypeId('');
    setSelectedFiles([]);
    setUploadedFiles([]);
    setUploadProgress(0);
    setError(prev => ({ ...prev, upload: null, selection: [] }));
    setReviewData(null); // Reset review data
    setLoadingReview(false);
    setReviewError(null);
    setUploadCompletedSuccessfully(false);
    setExpandedAssetGroupId(null); // Reset expanded group
  };
  
  // Close modal also resets the state
  const handleClose = () => {
    handleReset();
    onClose();
  };
  
  // Check if we can proceed to next step
  const canProceedToNextStep = () => {
    if (activeStep === 0) {
      return !!selectedCategoryId;
    } else if (activeStep === 1) {
      return !!selectedTypeId;
    }
    return true;
  };

  // File selection handlers
  // Validate format / size / cumulative count and report anything skipped.
  const addFiles = (incoming) => {
    const files = Array.from(incoming || []);
    if (files.length === 0) return;
    const { accepted, rejected } = validateFiles(files, selectedFiles, BATCH_UPLOAD_LIMITS);
    setError(prev => ({ ...prev, selection: buildRejectionSummary(rejected, BATCH_UPLOAD_LIMITS) }));
    if (accepted.length > 0) {
      setSelectedFiles(prev => [...prev, ...accepted]);
    }
  };

  const handleFileSelect = (event) => {
    addFiles(event.target.files);
    // Allow re-selecting the same file after it has been removed from the list
    event.target.value = '';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const removeFile = (indexToRemove) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  // Upload files to server
  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !batchId) {
      setError(prev => ({ ...prev, upload: 'Batch ID is missing. Cannot start upload.' }));
      return;
    }
    
    setLoading(prev => ({ ...prev, upload: true }));
    setError(prev => ({ ...prev, upload: null }));
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      
      // Append all selected files
      selectedFiles.forEach(file => {
        formData.append('photos', file);
      });
      
      // Append other metadata (batch_id is now in the URL)
      formData.append('selected_asset_category_id', selectedCategoryId);
      formData.append('selected_asset_type_id', selectedTypeId);
      
      // Create upload request with progress tracking, batchId in URL
      const response = await axios.post(
        `${mapConfig.apiBaseUrl}/inspections/batch-upload/${batchId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      setUploadedFiles(response.data.pending_photos || []);
      setSelectedFiles([]);
      setUploadCompletedSuccessfully(true); // Signal that upload finished
    } catch (err) {
      console.error('Error uploading files:', err);
      setUploadCompletedSuccessfully(false); // Ensure it's false on error
      setError(prev => ({
        ...prev,
        upload: describeUploadError(err, BATCH_UPLOAD_LIMITS, 'Failed to upload files')
      }));
    } finally {
      setLoading(prev => ({ ...prev, upload: false }));
      // Polling will be started by useEffect watching uploadCompletedSuccessfully
    }
  };

  // Function to start polling for photo statuses
  const startPollingPhotoStatuses = useCallback(() => {
    // This guard is still useful, though useEffect should also ensure these conditions.
    if (!batchId || uploadedFiles.length === 0) {
      return;
    }

    // Clear any existing interval before setting a new one
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    setIsPollingStatus(true);
    setPollingError(null);

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await axios.get(`${mapConfig.apiBaseUrl}/inspections/batch-upload/${batchId}/status`);
        const updatedPhotoStatuses = response.data;

        if (Array.isArray(updatedPhotoStatuses)) {
          let allTerminal = true;
          setUploadedFiles(prevFiles => 
            prevFiles.map(prevFile => {
              const updatedFile = updatedPhotoStatuses.find(p => p.id === prevFile.id || p.original_filename === prevFile.original_filename);
              if (updatedFile) {
                if (!['asset_matched', 'asset_not_found', 'finalized_to_inspection', 'omitted', 'metadata_extraction_failed', 'asset_matching_failed'].includes(updatedFile.status)) {
                  allTerminal = false;
                }
                return { ...prevFile, status: updatedFile.status, processing_notes: updatedFile.processing_notes, matched_asset_id: updatedFile.matched_asset_id };
              }
              // If not found in update, it means it's still processing or an issue, keep polling for it
              allTerminal = false; 
              return prevFile;
            })
          );

          if (allTerminal && updatedPhotoStatuses.length >= uploadedFiles.length) {
            // Check if all original files have received a terminal status update
            // This condition might need refinement if new files can appear during polling (not expected here)
            clearInterval(pollingIntervalRef.current);
            setIsPollingStatus(false);
          }
        } else {
           // If response is not an array, something is wrong, stop polling
           console.error('Invalid status update format from server:', updatedPhotoStatuses);
           throw new Error("Invalid status update format from server.");
        }
      } catch (err) {
        console.error('Error polling photo statuses:', err);
        setPollingError(err.response?.data?.error || err.message || 'Failed to update photo statuses.');
        clearInterval(pollingIntervalRef.current);
        setIsPollingStatus(false);
      }
    }, 5000); // Poll every 5 seconds

  }, [batchId, uploadedFiles]); // Depends on batchId and the uploadedFiles array itself for re-memoization

  // Effect to start polling after a successful upload
  useEffect(() => {
    if (uploadCompletedSuccessfully && batchId && uploadedFiles.length > 0) {
      startPollingPhotoStatuses();
      setUploadCompletedSuccessfully(false); // Reset the trigger
    }
  }, [uploadCompletedSuccessfully, batchId, uploadedFiles, startPollingPhotoStatuses]);

  // Effect to clear interval when component unmounts or modal closes
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Stop polling when modal is closed
  useEffect(() => {
    if (!open) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        setIsPollingStatus(false);
      }
    }
  }, [open]);

  // Fetch review data when entering the review step
  useEffect(() => {
    const fetchReviewData = async () => {
      if (activeStep === 3 && batchId && !reviewData) { // Step 3 is 'Review Batch'
        setLoadingReview(true);
        setReviewError(null);
        try {
          const response = await axios.get(`${mapConfig.apiBaseUrl}/inspections/batch-upload/${batchId}/review-summary`);
          setReviewData(response.data);
        } catch (err) {
          console.error('Error fetching review data:', err);
          setReviewError(err.response?.data?.error || err.message || 'Failed to load review data.');
        } finally {
          setLoadingReview(false);
        }
      }
    };

    fetchReviewData();
  }, [activeStep, batchId, reviewData]); // reviewData in deps to prevent re-fetch if already loaded

  const handleCreateInspectionForGroup = (assetIdToExpand) => {
    setExpandedAssetGroupId(prevExpandedId => 
      prevExpandedId === assetIdToExpand ? null : assetIdToExpand
    );
    // When a group is expanded, we might need to trigger fetching its specific inspection types/inspectors
    // This will be handled when we build the actual form component
  };

  const handleInspectionSubmitted = ({assetId, batchId, ...formData}) => {
    // Potentially update UI for this group to show "Details Submitted" or collapse
    setExpandedAssetGroupId(null); // Collapse the form after submission
    // We might need to re-fetch reviewData or update it locally if status changes
  };

  // Render categories dropdown with loading and error states
  const renderCategoriesDropdown = () => {
    if (loading.categories) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    
    if (error.categories) {
      return (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            my: 2, 
            bgcolor: alpha(theme.palette.error.light, 0.1),
            borderLeft: `4px solid ${theme.palette.error.main}`,
            borderRadius: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <ErrorOutlineIcon color="error" sx={{ mt: 0.5 }} />
            <Typography variant="body2" color="error">
              Error loading categories: {error.categories}
            </Typography>
          </Box>
        </Paper>
      );
    }
    
    return (
      <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
        <InputLabel id="batch-upload-category-label">Asset Category</InputLabel>
        <Select
          labelId="batch-upload-category-label"
          label="Asset Category"
          value={selectedCategoryId}
          onChange={handleCategoryChange}
        >
          <MenuItem value=""><em>Select a category</em></MenuItem>
          {categories.map((category) => (
            <MenuItem key={category.id} value={category.id}>
              {category.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };
  
  // Render types dropdown with loading and error states
  const renderTypesDropdown = () => {
    if (loading.types) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    
    if (error.types) {
      return (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            my: 2, 
            bgcolor: alpha(theme.palette.error.light, 0.1),
            borderLeft: `4px solid ${theme.palette.error.main}`,
            borderRadius: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <ErrorOutlineIcon color="error" sx={{ mt: 0.5 }} />
            <Typography variant="body2" color="error">
              Error loading types: {error.types}
            </Typography>
          </Box>
        </Paper>
      );
    }
    
    return (
      <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
        <InputLabel id="batch-upload-type-label">Asset Type</InputLabel>
        <Select
          labelId="batch-upload-type-label"
          label="Asset Type"
          value={selectedTypeId}
          onChange={handleTypeChange}
          disabled={!selectedCategoryId || types.length === 0} // Also disable if no types available for selected category
        >
          <MenuItem value=""><em>Select a type</em></MenuItem>
          {types.map((type) => (
            <MenuItem key={type.id} value={type.id}>
              {type.name}
            </MenuItem>
          ))}
        </Select>
        {selectedCategoryId && types.length === 0 && !loading.types && !error.types && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
            No types available for the selected category.
          </Typography>
        )}
      </FormControl>
    );
  };

  // Render file list
  const renderFileList = () => {
    if (selectedFiles.length === 0) return null;
    
    return (
      <Paper variant="outlined" sx={{ mt: 2, mb: 2, maxHeight: 200, overflow: 'auto' }}>
        <List dense disablePadding>
          {selectedFiles.map((file, index) => (
            <ListItem 
              key={index}
              secondaryAction={
                <IconButton edge="end" onClick={() => removeFile(index)} size="small">
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
              divider={index < selectedFiles.length - 1}
            >
              <ListItemIcon>
                <InsertDriveFileIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={file.name} 
                secondary={formatFileSize(file.size)}
              />
            </ListItem>
          ))}
        </List>
      </Paper>
    );
  };

  // Render upload progress
  const renderUploadProgress = () => {
    if (!loading.upload && uploadProgress === 0) return null;
    
    return (
      <Box sx={{ mt: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Uploading {selectedFiles.length} files...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {uploadProgress}%
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={uploadProgress} />
      </Box>
    );
  };

  // Render uploaded files
  const renderUploadedFiles = () => {
    if (uploadedFiles.length === 0) return null;
    
    return (
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} />
          <Typography variant="subtitle1" color="success.main">
            Files Uploaded Successfully
          </Typography>
          {isPollingStatus && <CircularProgress size={20} sx={{ ml: 2 }} />}
        </Box>
        {pollingError && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Could not update all live statuses: {pollingError}
          </Alert>
        )}
        
        <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
          <List dense disablePadding>
            {uploadedFiles.map((file, index) => (
              <ListItem
                key={file.id || index} // Use file.id if available from backend, else index
                divider={index < uploadedFiles.length - 1}
              >
                <ListItemIcon>
                  {file.status === 'asset_matched' || file.status === 'finalized_to_inspection' ? <CheckCircleOutlineIcon color="success" fontSize="small" /> :
                   file.status === 'asset_not_found' || file.status?.includes('_failed') ? <ErrorOutlineIcon color="error" fontSize="small" /> :
                   <InsertDriveFileIcon color="primary" fontSize="small" />
                  }
                </ListItemIcon>
                <ListItemText
                  primary={file.original_filename}
                  secondaryTypographyProps={{ component: 'div' }}
                  secondary={
                    <Box>
                      <Typography variant="caption" component="span">
                        Status: {file.status || 'Unknown'}
                      </Typography>
                      {file.processing_notes && (
                        <Typography variant="caption" display="block" sx={{fontSize: '0.7rem', color: 'text.secondary'}}>
                          Notes: {file.processing_notes}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Batch ID: {batchId}
        </Typography>
      </Box>
    );
  };

  const renderReviewStep = () => {
    if (loadingReview) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
          <Typography sx={{ml: 2}}>Loading review data...</Typography>
        </Box>
      );
    }
    if (reviewError) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>{reviewError}</Alert>
      );
    }
    if (reviewData) {
      return (
        <Box>
          {/* Matched Photo Groups */}
          {reviewData.matchedPhotoGroups && reviewData.matchedPhotoGroups.length > 0 && (
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>Matched Assets</Typography>
              {reviewData.matchedPhotoGroups.map((group, groupIndex) => (
                <Paper key={group.assetDetails.id || groupIndex} elevation={1} sx={{ p: 2, mb: 2, overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1">
                      Asset: {group.assetDetails.name} (ID: {group.assetDetails.id}, Type: {group.assetDetails.typeName})
                    </Typography>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={() => handleCreateInspectionForGroup(group.assetDetails.id)}
                    >
                      {expandedAssetGroupId === group.assetDetails.id ? 'Hide Details' : 'Provide Inspection Details'}
                    </Button>
                  </Box>
                  <List dense disablePadding sx={{mb: expandedAssetGroupId === group.assetDetails.id ? 1 : 0}}>
                    {group.photos.map((photo, photoIndex) => (
                      <ListItem key={photo.id || photoIndex} divider={photoIndex < group.photos.length -1}>
                        <ListItemIcon>
                          {photo.status === 'asset_matched' || photo.status === 'finalized_to_inspection' ? <CheckCircleOutlineIcon color="success" fontSize="small" /> :
                           <ErrorOutlineIcon color="warning" fontSize="small" />
                          }
                        </ListItemIcon>
                        <ListItemText 
                          primary={photo.original_filename} 
                          secondary={`Status: ${photo.status} ${photo.processing_notes ? ' - ' + photo.processing_notes : ''}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                  {/* Conditionally render Inspection Details Form Component */}
                  {expandedAssetGroupId === group.assetDetails.id && (
                     <InlineInspectionDetailsForm 
                       assetDetails={group.assetDetails} 
                       batchId={batchId}
                       onInspectionSubmit={handleInspectionSubmitted} // Callback after submission
                     />
                  )}
                </Paper>
              ))}
            </Box>
          )}

          {/* Unmatched Photos */}
          {reviewData.unmatchedPhotos && reviewData.unmatchedPhotos.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>Unmatched / Errored Photos</Typography>
              <Paper elevation={1} sx={{ p: 2 }}>
                <List dense disablePadding>
                  {reviewData.unmatchedPhotos.map((photo, photoIndex) => (
                    <ListItem key={photo.id || photoIndex} divider>
                      <ListItemIcon>
                        <ErrorOutlineIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={photo.original_filename} 
                        secondary={`Status: ${photo.status} ${photo.processing_notes ? ' - ' + photo.processing_notes : ''}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          )}
          {(reviewData.matchedPhotoGroups?.length === 0 && reviewData.unmatchedPhotos?.length === 0) && (
             <Typography>No photos found in this batch for review, or processing is still ongoing.</Typography>
          )}
        </Box>
      );
    }
    return null;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          maxHeight: '80vh',
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          p: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudUploadIcon color="primary" />
          <Typography variant="h6">Batch Upload Images</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {/* Step content */}
        <Box sx={{ mt: 2 }}>
          {activeStep === 0 && (
            <Box>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  mb: 3, 
                  bgcolor: alpha(theme.palette.info.light, 0.1),
                  borderLeft: `4px solid ${theme.palette.info.main}`,
                  borderRadius: 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <InfoOutlinedIcon color="info" sx={{ mt: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    Select the asset category for which you want to upload images. This helps in organizing and correctly associating images with the right asset type.
                  </Typography>
                </Box>
              </Paper>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CategoryIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="primary">
                  Select Asset Category
                </Typography>
              </Box>
              
              {renderCategoriesDropdown()}
            </Box>
          )}
          
          {activeStep === 1 && (
            <Box>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  mb: 3, 
                  bgcolor: alpha(theme.palette.info.light, 0.1),
                  borderLeft: `4px solid ${theme.palette.info.main}`,
                  borderRadius: 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <InfoOutlinedIcon color="info" sx={{ mt: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    Select the specific asset type. This further categorizes your images and helps in accurate data classification.
                  </Typography>
                </Box>
              </Paper>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CategoryIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="primary">
                  Select Asset Type
                </Typography>
              </Box>
              
              {renderTypesDropdown()}
            </Box>
          )}
          
          {activeStep === 2 && (
            <Box>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  mb: 3, 
                  bgcolor: alpha(theme.palette.info.light, 0.1),
                  borderLeft: `4px solid ${theme.palette.info.main}`,
                  borderRadius: 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <InfoOutlinedIcon color="info" sx={{ mt: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    Upload images for the selected {categories.find(c => c.id === selectedCategoryId)?.name || 'category'} &gt; {types.find(t => t.id === selectedTypeId)?.name || 'type'}. 
                    The system will automatically extract GPS metadata from these images if available.
                  </Typography>
                </Box>
              </Paper>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <ImageIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="primary">
                  Upload Images
                </Typography>
                <Chip 
                  label={`Batch ID: ${batchId.substring(0, 8)}...`} 
                  size="small" 
                  color="primary" 
                  variant="outlined" 
                  sx={{ ml: 2 }} 
                />
              </Box>
              
              {error.upload && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error.upload}
                </Alert>
              )}
              
              <Box 
                ref={dropZoneRef}
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  py: 5,
                  px: 3,
                  border: `2px dashed ${dragActive 
                    ? theme.palette.primary.main 
                    : alpha(theme.palette.primary.main, 0.3)}`,
                  borderRadius: 2,
                  bgcolor: dragActive 
                    ? alpha(theme.palette.primary.main, 0.08) 
                    : alpha(theme.palette.primary.main, 0.03),
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                  }
                }}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <CloudUploadIcon 
                  sx={{ 
                    fontSize: 60, 
                    color: theme.palette.primary.main,
                    opacity: 0.6,
                    mb: 2
                  }} 
                />
                <Typography variant="h6" gutterBottom>
                  Drag & Drop Images Here
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                  or
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  sx={{ borderRadius: 2 }}
                  disabled={loading.upload}
                >
                  Select Files
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    multiple
                    accept={buildAcceptAttribute(BATCH_UPLOAD_LIMITS)}
                    onChange={handleFileSelect}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                  {describeLimits(BATCH_UPLOAD_LIMITS)}
                </Typography>
              </Box>

              {Array.isArray(error.selection) && error.selection.length > 0 && (
                <Alert
                  severity="warning"
                  onClose={() => setError(prev => ({ ...prev, selection: [] }))}
                  sx={{ mt: 2 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Some files were skipped:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {error.selection.map((line, idx) => (
                      <Typography component="li" variant="body2" key={idx}>
                        {line}
                      </Typography>
                    ))}
                  </Box>
                </Alert>
              )}

              {selectedFiles.length > 0 && (
                <Typography
                  variant="body2"
                  color={selectedFiles.length >= BATCH_UPLOAD_LIMITS.maxFiles ? 'warning.main' : 'text.secondary'}
                  sx={{ mt: 2, fontWeight: selectedFiles.length >= BATCH_UPLOAD_LIMITS.maxFiles ? 600 : 400 }}
                >
                  {selectedFiles.length} / {BATCH_UPLOAD_LIMITS.maxFiles} files selected · {formatFileSize(selectedFiles.reduce((sum, f) => sum + (f.size || 0), 0))}
                  {selectedFiles.length >= BATCH_UPLOAD_LIMITS.maxFiles && ' · limit reached'}
                </Typography>
              )}

              {renderFileList()}
              {renderUploadProgress()}
              {renderUploadedFiles()}
              
              {selectedFiles.length > 0 && !loading.upload && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpload}
                    startIcon={<CloudUploadIcon />}
                    disabled={loading.upload}
                    sx={{ borderRadius: 2 }}
                  >
                    Upload {selectedFiles.length} Files
                  </Button>
                </Box>
              )}
            </Box>
          )}
          
          {activeStep === 3 && (
            <Box>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  mb: 3, 
                  bgcolor: alpha(theme.palette.info.light, 0.1),
                  borderLeft: `4px solid ${theme.palette.info.main}`,
                  borderRadius: 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <InfoOutlinedIcon color="info" sx={{ mt: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    Review the processed photos below. Photos are grouped by matched assets. You can then proceed to provide inspection details for each group.
                  </Typography>
                </Box>
              </Paper>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CategoryIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="primary">
                  Review Batch: {reviewData?.batch_id || batchId}
                </Typography>
              </Box>

              {renderReviewStep()}
              {loadingReview && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                  <CircularProgress />
                  <Typography sx={{ml: 2}}>Loading review data...</Typography>
                </Box>
              )}
              {reviewError && (
                <Alert severity="error" sx={{ mb: 2 }}>{reviewError}</Alert>
              )}
            </Box>
          )}
        </Box>
        
        {/* Navigation buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
          <Button
            variant="outlined"
            onClick={activeStep === 0 ? handleClose : handleBack}
            sx={{ borderRadius: 2 }}
            disabled={loading.upload}
          >
            {activeStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={activeStep === steps.length - 1 ? handleClose : handleNext}
            disabled={!canProceedToNextStep() || loading.categories || loading.types || loading.upload}
            sx={{ borderRadius: 2 }}
          >
            {activeStep === steps.length - 1 ? 'Done' : 'Next'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BatchUploadModal;